import { AddCircle, Favorite, Send, MusicNote, PlayArrow, MoreVert } from '@mui/icons-material';
import { Avatar, Box, Card, IconButton, Typography, useMediaQuery, useTheme } from '@mui/material';
import { keyframes } from '@mui/system';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

const progressAnimation = keyframes`
  0% { width: 0%; }
  100% { width: 100%; }
`;

const SnapchatVideoPreview = ({ data }) => {
  const { t: translate } = useTranslation();
  const theme = useTheme();
  const videoRef = useRef(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const profile = data.connected_profile_details || {};
  const title = data.title;

  useEffect(() => {
    if (videoRef.current) {
      videoRef.current.muted = true; // Start muted (browser policy)
    }
  }, []);

  const togglePlay = () => {
    const video = videoRef.current;
    if (!video) return;

    if (isPlaying) {
      video.pause();
    } else {
      // Unmute before playing (important for user gesture)
      video.muted = false;
      video.play()
        .then(() => {
          setIsPlaying(true);
        })
        .catch((err) => {
          console.error('Playback failed:', err);
          // Fallback: Try muted playback if autoplay blocked
          video.muted = true;
          video.play().then(() => setIsPlaying(true)).catch(console.error);
        });
    }
  };

  // Optional: Unmute on first user interaction with the card
  const handleCardClick = () => {
    if (!isPlaying && videoRef.current) {
      videoRef.current.muted = false;
    }
  };

  return (
    <Box display="flex" justifyContent="center" alignItems="center" p={1}>
      <Card
        onClick={handleCardClick}
        sx={{
          width: isMobile ? 300 : 340,
          height: 580,
          borderRadius: '18px',
          position: 'relative',
          overflow: 'hidden',
          bgcolor: '#0a0a0a',
          boxShadow: '0 8px 25px rgba(0,0,0,0.5)',
          cursor: 'pointer',
        }}
      >
        {/* Top Bar */}
        <Box sx={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          zIndex: 10,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          p: '10px 14px',
          background: 'linear-gradient(to bottom, rgba(0,0,0,0.75), transparent)'
        }}>
          <Box display="flex" alignItems="center" gap={1.2}>
            <Avatar 
              src={profile.connected_profile_image} 
              sx={{ width: 30, height: 30, border: '2px solid white' }} 
            />
            <Box>
              <Typography variant="subtitle2" color="white" fontWeight={600} fontSize={14}>
                {profile.connected_profile_name}
              </Typography>
              <Typography variant="caption" color="white" sx={{ opacity: 0.85, fontSize: 12 }}>
                {translate('preview.just_now')}
              </Typography>
            </Box>
          </Box>
          <IconButton sx={{ color: 'white', p: 0.5 }}><MoreVert /></IconButton>
        </Box>

        {/* Video */}
        <video
          ref={videoRef}
          src={data.media_url}
          style={{ 
            width: '100%', 
            height: '100%', 
            objectFit: 'cover' 
          }}
          muted
          playsInline
          loop
          onClick={togglePlay}
        />

        {/* Play Button Overlay */}
        {!isPlaying && (
          <IconButton
            onClick={togglePlay}
            sx={{
              position: 'absolute',
              top: '50%',
              left: '50%',
              transform: 'translate(-50%, -50%)',
              bgcolor: 'rgba(0,0,0,0.65)',
              '&:hover': { bgcolor: 'rgba(0,0,0,0.85)' },
              width: 62,
              height: 62,
            }}
          >
            <PlayArrow sx={{ color: 'white', fontSize: 42 }} />
          </IconButton>
        )}

        {/* Title */}
        {title && (
          <Box sx={{ 
            position: 'absolute', 
            top: 65, 
            left: 16, 
            right: 16, 
            zIndex: 9,
            bgcolor: 'rgba(0,0,0,0.6)',
            borderRadius: 1,
            p: 1
          }}>
            <Typography variant="subtitle1" color="white" fontWeight={700}>
              {title}
            </Typography>
          </Box>
        )}

        {/* Description */}
        {data.description && (
          <Box sx={{
            position: 'absolute',
            bottom: 100,
            left: 16,
            right: 16,
            color: 'white',
            textShadow: '0 1px 3px rgba(0,0,0,0.8)'
          }}>
            <Typography
              variant="body2"
              sx={{
                display: '-webkit-box',
                WebkitLineClamp: 3,
                WebkitBoxOrient: 'vertical',
                overflow: 'hidden',
                fontSize: 14.5,
                lineHeight: 1.35
              }}
            >
              {data.description}
            </Typography>
          </Box>
        )}

        {/* Bottom UI */}
        <Box sx={{
          position: 'absolute',
          bottom: 16,
          left: 16,
          right: 16,
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'flex-end'
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, color: 'white' }}>
            <MusicNote sx={{ fontSize: 17 }} />
            <Typography variant="caption" sx={{ fontSize: 12.5 }}>Original Sound</Typography>
          </Box>

          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1.2 }}>
            <IconButton sx={{ color: 'white', p: 0.5 }}>
              <Favorite sx={{ fontSize: 30 }} />
            </IconButton>
            <IconButton sx={{ color: 'white', p: 0.5 }}>
              <Send sx={{ fontSize: 26, transform: 'rotate(-45deg)' }} />
            </IconButton>

            <Avatar
              src={profile.connected_profile_image}
              sx={{ width: 38, height: 38, border: '2.5px solid white' }}
            >
              <AddCircle sx={{ position: 'absolute', bottom: -3, right: -3, color: '#00ff00', fontSize: 16 }} />
            </Avatar>
          </Box>
        </Box>
      </Card>
    </Box>
  );
};

export default SnapchatVideoPreview;